import { Menu, Popover, Select, UnstyledButton } from '@mantine/core';
import { IconBell, IconChevronDown, IconSearch } from '@tabler/icons-react';
import { type ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { allMenuItems, MENU, type MenuItem } from './menu.js';
import { PersonAvatar } from './PersonAvatar.js';
import classes from './TopBar.module.css';

/** Aramada menü dışındaki sonuçlar (gemiler, kişiler): değer gidilecek yoldur. */
export interface SearchGroup {
  group: string;
  items: readonly { path: string; label: string }[];
}

export interface TopBarProps {
  onNavigate: (path: string) => void;
  searchGroups?: readonly SearchGroup[] | undefined;
  notifications: { count: number; content: ReactNode };
  /** `detail`: rol ya da rütbe (barda); `email` yalnız açılır menünün başlığında (M6f G1-11). */
  user: { name: string; detail: string; email?: string | undefined };
  /** Kullanıcı menüsünün öğeleri (`Menu.Item`); dil seçimi de burada. */
  userMenu: ReactNode;
  /** Dar ekranda menüyü açan düğme. */
  burger?: ReactNode;
  /** Aramadaki modüller bu menüden; varsayılan müşteri menüsü (ADR 0039 §14). */
  menu?: readonly MenuItem[] | undefined;
  /** Arama kutusunun yer tutucusu; varsayılan `shell.search`. */
  searchPlaceholder?: string | undefined;
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return (
    (parts[0]?.[0] ?? '') + (parts.length > 1 ? (parts.at(-1)?.[0] ?? '') : '')
  ).toLocaleUpperCase();
}

/**
 * Üst bar (BGM tasarımı): beyaz zemin; solda genel arama, sağda bildirim zili (sayı rozeti) ve
 * kullanıcı (avatar, ad, alt satır, açılır menü).
 */
export function TopBar({
  onNavigate,
  searchGroups = [],
  notifications,
  user,
  userMenu,
  burger,
  menu = MENU,
  searchPlaceholder,
}: TopBarProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');

  const data = useMemo(() => {
    const modules = allMenuItems(menu)
      // Menüde görünmeyen yollar aramada da yok: aynı ad iki kez çıkmaz (ADR 0082)
      .filter((item) => !item.children && !item.offMenu)
      .map((item) => ({ value: item.path, label: t(`menu.${item.id}`) }));
    const seen = new Set(modules.map((item) => item.value));
    const groups = [{ group: t('shell.searchModules'), items: modules }];
    for (const extra of searchGroups) {
      const items = extra.items
        .filter((item) => !seen.has(item.path))
        .map((item) => {
          seen.add(item.path);
          return { value: item.path, label: item.label };
        });
      if (items.length > 0) groups.push({ group: extra.group, items });
    }
    return groups;
  }, [searchGroups, t, menu]);

  const count = notifications.count;
  return (
    <div className={classes.root}>
      {burger}
      <Select
        className={classes.search}
        classNames={{ input: classes.searchInput }}
        data={data}
        value={null}
        searchable
        searchValue={search}
        onSearchChange={setSearch}
        onChange={(value) => {
          setSearch('');
          if (value) onNavigate(value);
        }}
        placeholder={searchPlaceholder ?? t('shell.search')}
        aria-label={t('shell.searchLabel')}
        nothingFoundMessage={t('shell.searchEmpty')}
        leftSection={<IconSearch size={16} aria-hidden />}
        rightSection={<span />}
        rightSectionPointerEvents="none"
        withCheckIcon={false}
        size="sm"
        radius="sm"
        comboboxProps={{ withinPortal: true }}
        maxDropdownHeight={360}
      />

      <div className={classes.right}>
        <Popover position="bottom-end" width={320} shadow="md" withinPortal>
          <Popover.Target>
            <UnstyledButton
              className={classes.bell}
              aria-label={
                count > 0 ? t('shell.notificationsCount', { count }) : t('shell.notifications')
              }
            >
              <IconBell size={24} stroke={1.6} aria-hidden />
              {count > 0 ? (
                <span className={classes.count} aria-hidden>
                  {count > 99 ? '99+' : count}
                </span>
              ) : null}
            </UnstyledButton>
          </Popover.Target>
          <Popover.Dropdown className={classes.dropdown}>
            <p className={classes.dropdownTitle}>{t('shell.notifications')}</p>
            {notifications.content}
          </Popover.Dropdown>
        </Popover>

        <Menu position="bottom-end" width={240} withinPortal>
          <Menu.Target>
            <UnstyledButton className={classes.user} aria-label={t('shell.userMenu')}>
              {/* Kişi görseli ortak: açık mavi daire, baş harfler (M6f G1-20) */}
              <PersonAvatar name={user.name} size={38} />
              <span className={classes.userText}>
                <span className={classes.userName} title={user.name}>
                  {user.name}
                </span>
                {user.detail ? (
                  <span className={classes.userDetail} title={user.detail}>
                    {user.detail}
                  </span>
                ) : null}
              </span>
              <IconChevronDown size={16} className={classes.chevron} aria-hidden />
            </UnstyledButton>
          </Menu.Target>
          <Menu.Dropdown>
            {user.email ? (
              <>
                <div className={classes.menuHeader}>
                  <span className={classes.menuName}>{user.name}</span>
                  <span className={classes.menuEmail}>{user.email}</span>
                </div>
                <Menu.Divider />
              </>
            ) : null}
            {userMenu}
          </Menu.Dropdown>
        </Menu>
      </div>
    </div>
  );
}
